import { slugify } from "@certn/utils";
import { css, cx } from "@emotion/css";
import { Key, useEffect, useMemo, useState } from "react";
import { useFilter } from "react-aria";
import { Dialog, ListBoxItem } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { PickerFooter } from "./PickerFooter";
import { PickerListItem } from "./PickerListItem";
import { MediaQuery } from "../../../utils/MediaQuery";
import { Divider } from "../../divider/Divider";
import { Loader } from "../../loaders/loading/Loader";
import { DialogHeader } from "../../modal/DialogHeader";
import { Search } from "../../search/Search";
import { mq } from "../../utils";
import { PickerProps } from "../Picker";
import { PickerListBox } from "../PickerListBox";
import { PickerItem } from "../types";

interface Props<T> extends PickerProps<T> {
  onClose: () => void;
  onDone?: (selected: Key[]) => void;
  actionText?: string;
  hideClear?: boolean;
  truncateItem?: boolean;
}

export function PickerDialog<T extends PickerItem>(props: Props<T>) {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [selection, setSelection] = useState<PickerProps<T>["selectedKeys"]>(
    new Set([])
  );

  const {
    isLoading,
    label = t("Select an option"),
    showActionBar,
    testID = "picker",
    trigger,
    maxWidth,
    actionText,
    hideClear,
    onDone,
    truncateItem,
    ...listBoxProps
  } = props;

  const { contains } = useFilter({ sensitivity: "base" });

  const filteredItems = useMemo(() => {
    if (!search || !props.items) {
      return props.items || [];
    }

    return [...props.items].filter((item) => {
      return contains(item.name, search);
    });
  }, [contains, props.items, search]);

  useEffect(() => {
    if (props.selectedKeys) {
      setSelection(props.selectedKeys);
    }
  }, [props.selectedKeys]);

  const searchable = props.items && [...props.items].length > 10;
  const clearable = selection && Array.from(selection).length > 0 && !hideClear;

  const handleDone = () => {
    if (onDone) {
      onDone(Array.from(selection || []));
      props.onClose?.();
      return;
    }

    props.onSelectionChange?.(new Set(selection));
    props.onClose?.();
  };

  return (
    <Dialog
      data-testid={`${testID}-dialog`}
      className={cx(styles.dialogHeight, "react-aria-Dialog")}
    >
      <MediaQuery sm>
        <DialogHeader
          onClose={props?.onClose}
          hideDivider
          testID={`${testID}-header`}
        >
          {label}
        </DialogHeader>

        {!searchable && <Divider />}
      </MediaQuery>
      {searchable && (
        <div className={styles.header}>
          <Search
            aria-label={t("Search")}
            name="search"
            onChange={setSearch}
            placeholder={t("Search")}
            testID={`${testID}-search`}
            value={search}
            compact={false}
            maxWidth="unset"
          />
        </div>
      )}
      <PickerListBox
        aria-label={label}
        {...listBoxProps}
        selectedKeys={selection}
        onSelectionChange={(selection) => {
          if (showActionBar) {
            setSelection(selection);
          } else {
            props.onSelectionChange?.(selection);
            props.onClose?.();
          }
        }}
        items={filteredItems}
        className={styles.pickerDialog}
      >
        {(item) => {
          return (
            <ListBoxItem textValue={item.name} className={styles.listBoxItem}>
              {(state) => {
                return (
                  <PickerListItem
                    isSelected={state.isSelected}
                    leading={item.leading}
                    selectionMode={state.selectionMode}
                    subtitle={item.subtitle}
                    testID={`${testID}-${slugify(item.name)}`}
                    title={item.name}
                    truncateTitle={props.truncateItem}
                  />
                );
              }}
            </ListBoxItem>
          );
        }}
      </PickerListBox>
      {showActionBar && (
        <PickerFooter
          onDone={handleDone}
          clearable={clearable}
          onClear={() => {
            setSelection(new Set([]));
            props.onSelectionChange?.(new Set([]));
            props.onClose?.();
          }}
          action={actionText ?? t("Done")}
          testID={`${testID}-footer`}
        />
      )}
      {!showActionBar && (
        <MediaQuery sm>
          <PickerFooter
            onDone={props.onClose}
            action={t("Close")}
            testID={testID}
          />
        </MediaQuery>
      )}
      {props.isLoading && <Loader overlay background="surface-overlay" />}
    </Dialog>
  );
}

const styles = {
  header: css({
    borderBottomColor: "var(--border-base)",
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    paddingBottom: "var(--space-3)",
    paddingLeft: "var(--space-4)",
    paddingRight: "var(--space-4)",
    paddingTop: "var(--space-3)",
  }),
  listBoxItem: css({
    outline: "none",
    cursor: "pointer",
    borderRadius: "var(--border-radius-2)",
    "&[data-hovered]": {
      backgroundColor: "var(--background-ghost-hover)",
    },
    "&[data-focus-visible]": {
      boxShadow: "var(--focus-action)",
    },
  }),
  pickerDialog: css({ padding: "var(--space-1)", overflowY: "auto" }),
  dialogHeight: css(
    mq({
      maxHeight: [undefined, "400px"],
    })
  ),
};
